import React, { useEffect, useState } from "react";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import TextField from "@material-ui/core/TextField";

export default function SetPlayerAsVerifiedDialog({
  openState,
  userContext,
  onClose,
  onSetPlayerVerified,
}) {
  const [btnDisabled, setBtnDisabled] = useState(false);
  const [error, setError] = useState("");
  const [refreshNotice, setRefreshNotice] = useState("");
  const [bypassConfirmed, setBypassConfirmed] = useState(false);
  const [overrideConfirmed, setOverrideConfirmed] = useState(false);
  const [overrideReason, setOverrideReason] = useState("");

  useEffect(() => {
    if (openState) {
      setBtnDisabled(false);
      setError("");
      setRefreshNotice("");
      setBypassConfirmed(false);
      setOverrideConfirmed(false);
      setOverrideReason("");
    }
  }, [openState, userContext?.userId]);

  if (!("row" in userContext)) {
    return <></>;
  }

  const row = userContext.row;
  const isPendingReview = row?.accountStatus === "pending_review";
  const isNotEligible = row?.accountStatus === "noteligible";
  const phoneVerified = row?.phoneVerification?.status === "verified";
  const hasVerifiedEmail = row?.emailIsSynthetic !== true && Boolean(row?.emailVerifiedAt);
  const effectiveCountry = row?.country || row?.geoDetectedCountry || row?.effectiveCountry || null;

  const needsBypass = isPendingReview && !phoneVerified;
  const trimmedOverrideReason = overrideReason.trim();
  const canSubmit =
    (isPendingReview && (phoneVerified || bypassConfirmed)) ||
    (isNotEligible && overrideConfirmed && trimmedOverrideReason.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }
    setBtnDisabled(true);
    setError("");
    setRefreshNotice("");

    const result = await onSetPlayerVerified(userContext.row.userId, {
      bypassPhoneVerification: needsBypass,
      overrideNotEligible: isNotEligible,
      overrideReason: isNotEligible ? trimmedOverrideReason : "",
    });
    if (result?.ok) {
      if (result?.refreshError) {
        setRefreshNotice(
          "Player marked as verified, but the user list failed to refresh. Please refresh before making more changes."
        );
        setBtnDisabled(false);
        return;
      }

      onClose();
      return;
    }

    setError(result?.error || "Failed to set player as verified");
    setBtnDisabled(false);
  };

  return (
    <Dialog
      open={openState}
      onClose={btnDisabled ? undefined : onClose}
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="form-dialog-title">
        Set {userContext.row.nickname} as Verified
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          By clicking &quot;Save Changes&quot; you will approve the player{" "}
          <strong>{userContext.row.nickname}</strong> so that they can queue
          for matches.
        </DialogContentText>
        <DialogContentText style={{ marginTop: 8 }}>
          This means you have verified their FaceIT, Steam, and all other
          details.
        </DialogContentText>

        {isNotEligible && (
          <div
            style={{
              marginTop: 16,
              padding: "12px 16px",
              background: "rgba(211, 47, 47, 0.08)",
              border: "1px solid #d32f2f",
              borderRadius: 6,
            }}
          >
            <Typography
              variant="body2"
              style={{ color: "#d32f2f", fontWeight: 600, marginBottom: 6 }}
            >
              Not eligible override
            </Typography>
            <Typography variant="body2" style={{ color: "#d32f2f" }}>
              This player is currently marked as not eligible. Verifying them
              will let them queue for matches and close their eligibility appeal
              thread.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              margin="normal"
              variant="outlined"
              label="Override reason"
              value={overrideReason}
              onChange={(e) => setOverrideReason(e.target.value)}
              disabled={btnDisabled}
              helperText="Briefly explain why this not-eligible decision is being overturned."
            />
            <FormControlLabel
              style={{ marginTop: 4 }}
              control={
                <Checkbox
                  checked={overrideConfirmed}
                  onChange={(e) => setOverrideConfirmed(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  I confirm this member should be overturned from not eligible
                  to verified
                </Typography>
              }
            />
          </div>
        )}

        {phoneVerified && (
          <Typography
            variant="body2"
            style={{ marginTop: 12, color: "#2e7d32" }}
          >
            Phone verification is complete for this player.
          </Typography>
        )}

        {!hasVerifiedEmail && (
          <Typography variant="body2" style={{ marginTop: 12, color: "#ed6c02" }}>
            No verified email is on file. The player must sign back in to check
            their approval status.
          </Typography>
        )}

        {effectiveCountry && !row?.country && (
          <Typography variant="body2" style={{ marginTop: 8, color: "#ed6c02" }}>
            {row?.geoDetectedCountryFlagImageUrl ? (
              <img
                src={row.geoDetectedCountryFlagImageUrl}
                alt=""
                style={{
                  width: 18,
                  height: 13,
                  marginRight: 6,
                  verticalAlign: "middle",
                  borderRadius: 2,
                }}
              />
            ) : null}
            Country is inferred from GeoIP:{" "}
            <strong>
              {row?.geoDetectedCountryDisplayName || effectiveCountry}
              {row?.geoDetectedCountryDisplayName
                ? ` (${effectiveCountry})`
                : ""}
            </strong>
            .
          </Typography>
        )}

        {needsBypass && (
          <div
            style={{
              marginTop: 16,
              padding: "12px 16px",
              background: "rgba(237, 108, 2, 0.08)",
              border: "1px solid #ed6c02",
              borderRadius: 6,
            }}
          >
            <Typography
              variant="body2"
              style={{ color: "#ed6c02", fontWeight: 600, marginBottom: 6 }}
            >
              Phone verification not complete
            </Typography>
            <Typography variant="body2" style={{ color: "#ed6c02" }}>
              This player has not completed SMS phone verification (current
              status:{" "}
              <strong>
                {row?.phoneVerification?.status ?? "none"}
              </strong>
              ). This may be due to SMS delivery problems or a manual
              verification process.
            </Typography>
            <Typography
              variant="body2"
              style={{ color: "#ed6c02", marginTop: 6 }}
            >
              If you have verified this player&apos;s identity through another
              means (e.g. video call, in-person), tick the checkbox below to
              override and proceed.
            </Typography>
            <FormControlLabel
              style={{ marginTop: 10 }}
              control={
                <Checkbox
                  checked={bypassConfirmed}
                  onChange={(e) => setBypassConfirmed(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  I have manually verified this player&apos;s identity and
                  authorise bypassing phone verification
                </Typography>
              }
            />
          </div>
        )}

        {error ? (
          <Typography variant="body2" style={{ marginTop: 12, color: "#d32f2f" }}>
            {error}
          </Typography>
        ) : null}
        {refreshNotice ? (
          <Typography variant="body2" style={{ marginTop: 12, color: "#ed6c02" }}>
            {refreshNotice}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary" disabled={btnDisabled}>
          Dismiss
        </Button>
        <Button
          disabled={btnDisabled || Boolean(refreshNotice) || !canSubmit}
          onClick={handleSubmit}
          color="primary"
        >
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
